import { useEffect, useState } from "react";
import { CalendarBlankIcon, CaretDownIcon } from "@phosphor-icons/react";

import { PRESETS, presetRange } from "../../lib/reports";

/**
 * DateRangePicker — preset dropdown + custom date inputs.
 *
 * Controlled component. Props:
 *   value: { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', preset?: string }
 *   onChange: (value) => void
 */
export default function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value.from);
  const [customTo, setCustomTo] = useState(value.to);

  useEffect(() => {
    setCustomFrom(value.from);
    setCustomTo(value.to);
  }, [value.from, value.to]);

  const activePreset = PRESETS.find((p) => p.key === value.preset);
  const label = activePreset
    ? activePreset.label
    : `${value.from} → ${value.to}`;

  const choosePreset = (key) => {
    const r = presetRange(key);
    onChange({ from: r.from, to: r.to, preset: key });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    if (customFrom > customTo) return;
    onChange({ from: customFrom, to: customTo, preset: "custom" });
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="reports-date-range">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="reports-date-range-toggle"
        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
      >
        <CalendarBlankIcon size={14} weight="bold" className="text-slate-500" />
        <span>{label}</span>
        <CaretDownIcon size={12} weight="bold" className="text-slate-400" />
      </button>

      {open && (
        <>
          {/* outside click trap */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 z-40 mt-2 w-72 rounded-md border border-slate-200 bg-white shadow-lg">
            <ul className="py-1.5">
              {PRESETS.map((p) => (
                <li key={p.key}>
                  <button
                    type="button"
                    onClick={() => choosePreset(p.key)}
                    data-testid={`reports-preset-${p.key}`}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 ${
                      value.preset === p.key
                        ? "font-semibold text-amber-700"
                        : "text-slate-700"
                    }`}
                  >
                    {p.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-200 p-3">
              <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">
                Custom range
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid="reports-custom-from"
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs font-data outline-none focus:border-amber-500"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid="reports-custom-to"
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs font-data outline-none focus:border-amber-500"
                />
              </div>
              <button
                type="button"
                onClick={applyCustom}
                disabled={!customFrom || !customTo || customFrom > customTo}
                data-testid="reports-custom-apply"
                className="mt-2 w-full rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
